import { getSubjectByCode } from '@/data/subjectsCatalog';
import { LinearGradient } from 'expo-linear-gradient';
import { ChatCircleDots, X } from 'phosphor-react-native';
import { Dimensions, Modal, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 500; // Largeur max pour le modal

const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber500: '#F59E0B',
};

interface SubjectOption {
  code: string;
  label: string;
  diploma: string;
  section: string;
}

interface SelectSubjectModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (subject: SubjectOption) => void;
  subjectCodes: string[];
  isLoading: boolean;
  existingSubjectCodes?: string[];
}

export default function SelectSubjectModal({
  visible,
  onClose,
  onSelect,
  subjectCodes,
  isLoading,
  existingSubjectCodes = [],
}: SelectSubjectModalProps) {
  // Transformer les codes en options avec labels
  const subjects: SubjectOption[] = subjectCodes
    .map((code) => {
      const entry = getSubjectByCode(code);
      if (!entry) return null;
      return {
        code: entry.subject_code,
        label: entry.subject,
        diploma: entry.diploma,
        section: entry.section,
      };
    })
    .filter((s): s is SubjectOption => s !== null);

  // Séparer les matières disponibles et celles déjà utilisées
  const availableSubjects = subjects.filter(
    (s) => !existingSubjectCodes.includes(s.code)
  );
  const usedSubjects = subjects.filter((s) =>
    existingSubjectCodes.includes(s.code)
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWeb ? "fade" : "slide"}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity 
          style={styles.overlayTouchable} 
          activeOpacity={1} 
          onPress={onClose}
        />
        
        {/* ✅ Wrapper pour centrer sur web */}
        <View style={styles.containerWrapper}>
          <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerHandle} />
              <View style={styles.headerContent}>
                <LinearGradient
                  colors={[colors.indigo100, colors.indigo50]}
                  style={styles.headerIcon}
                >
                  <ChatCircleDots size={24} color={colors.indigo600} weight="duotone" />
                </LinearGradient>
                <View style={styles.headerTextContainer}>
                  <Text style={styles.headerTitle}>Nouvelle conversation</Text>
                  <Text style={styles.headerSubtitle}>Choisis une matière à réviser</Text>
                </View>
                <TouchableOpacity
                  onPress={onClose}
                  style={styles.closeButton}
                  activeOpacity={0.7}
                  disabled={isLoading}
                >
                  <X size={22} color={colors.slate500} weight="bold" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Content */}
            <ScrollView
              style={styles.content}
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
            >
              {subjects.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>Aucune matière configurée</Text>
                  <Text style={styles.emptySubtitle}>
                    Va dans ton profil pour sélectionner tes matières.
                  </Text>
                </View>
              ) : (
                <>
                  {/* Matières disponibles */}
                  {availableSubjects.length > 0 && (
                    <View style={styles.section}>
                      {usedSubjects.length > 0 && (
                        <Text style={styles.sectionTitle}>Disponibles</Text>
                      )}
                      {availableSubjects.map((subject) => (
                        <TouchableOpacity
                          key={subject.code}
                          style={styles.subjectCard}
                          onPress={() => onSelect(subject)}
                          activeOpacity={0.7}
                          disabled={isLoading}
                        >
                          <LinearGradient
                            colors={[colors.indigo100, colors.indigo50]}
                            style={styles.subjectIcon}
                          >
                            <ChatCircleDots
                              size={22}
                              color={colors.indigo600}
                              weight="duotone"
                            />
                          </LinearGradient>
                          <View style={styles.subjectInfo}>
                            <Text style={styles.subjectLabel} numberOfLines={2}>
                              {subject.label}
                            </Text>
                            <Text style={styles.subjectMeta}>
                              {subject.diploma} {subject.section}
                            </Text>
                          </View>
                          {isLoading ? (
                            <ActivityIndicator size="small" color={colors.indigo600} />
                          ) : (
                            <View style={styles.chevron}>
                              <Text style={styles.chevronText}>→</Text>
                            </View>
                          )}
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}

                  {/* Matières déjà utilisées */}
                  {usedSubjects.length > 0 && (
                    <View style={styles.section}>
                      <Text style={styles.sectionTitle}>Déjà en cours</Text>
                      <Text style={styles.sectionHint}>
                        Tu as déjà une conversation pour ces matières
                      </Text>
                      {usedSubjects.map((subject) => (
                        <View key={subject.code} style={styles.subjectCardDisabled}>
                          <View style={styles.subjectIconDisabled}>
                            <ChatCircleDots
                              size={22}
                              color={colors.slate400}
                              weight="duotone"
                            />
                          </View>
                          <View style={styles.subjectInfo}>
                            <Text style={styles.subjectLabelDisabled} numberOfLines={2}>
                              {subject.label}
                            </Text>
                            <Text style={styles.subjectMetaDisabled}>
                              {subject.diploma} {subject.section}
                            </Text>
                          </View>
                          <View style={styles.usedBadge}>
                            <Text style={styles.usedBadgeText}>✓</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Message si toutes les matières sont utilisées */}
                  {availableSubjects.length === 0 && usedSubjects.length > 0 && (
                    <View style={styles.allUsedMessage}>
                      <Text style={styles.allUsedEmoji}>🎉</Text>
                      <Text style={styles.allUsedText}>
                        Tu as déjà une conversation pour chaque matière !
                      </Text>
                      <Text style={styles.allUsedHint}>
                        Retourne sur une conversation existante pour continuer.
                      </Text>
                    </View>
                  )}
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    justifyContent: isWeb ? 'center' : 'flex-end', // ✅ Centré sur web
    alignItems: isWeb ? 'center' : undefined, // ✅ Centré sur web
  },
  overlayTouchable: {
    ...StyleSheet.absoluteFillObject, // ✅ Couvre tout l'écran
  },
  // ✅ Wrapper pour positionner le modal
  containerWrapper: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    maxHeight: isWeb ? '80%' : undefined,
    alignItems: 'center',
  },
  container: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    // ✅ Sur web, coins arrondis partout
    ...(isWeb && {
      borderBottomLeftRadius: 28,
      borderBottomRightRadius: 28,
    }),
    width: '100%',
    height: isWeb ? undefined : SCREEN_HEIGHT * 0.75,
    maxHeight: isWeb ? 600 : undefined,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: -8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
    overflow: 'hidden',
  },
  header: {
    paddingTop: 12,
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate100,
  },
  headerHandle: {
    width: 40,
    height: 4,
    backgroundColor: colors.slate300,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
    // ✅ Masquer sur web (pas de drag)
    ...(isWeb && {
      display: 'none',
    }),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextContainer: {
    flex: 1,
    marginLeft: 14,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.slate900,
    letterSpacing: -0.3,
  },
  headerSubtitle: {
    fontSize: 14,
    color: colors.slate500,
    marginTop: 2,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.slate500,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionHint: {
    fontSize: 13,
    color: colors.slate400,
    marginBottom: 12,
    marginTop: -4,
  },
  subjectCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 14,
    marginBottom: 10,
    borderWidth: 2,
    borderColor: colors.slate200,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  subjectCardDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.slate50,
    borderRadius: 16,
    padding: 14,
    marginBottom: 10,
    borderWidth: 2,
    borderColor: colors.slate200,
    opacity: 0.6,
  },
  subjectIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  subjectIconDisabled: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.slate200,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  subjectInfo: {
    flex: 1,
    gap: 4,
  },
  subjectLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate900,
    lineHeight: 20,
  },
  subjectLabelDisabled: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate500,
    lineHeight: 20,
  },
  subjectMeta: {
    fontSize: 13,
    color: colors.indigo600,
    fontWeight: '600',
  },
  subjectMetaDisabled: {
    fontSize: 13,
    color: colors.slate400,
    fontWeight: '500',
  },
  chevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.indigo100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevronText: {
    fontSize: 16,
    color: colors.indigo600,
    fontWeight: '600',
  },
  usedBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.slate200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  usedBadgeText: {
    fontSize: 14,
    color: colors.slate500,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 20,
  },
  allUsedMessage: {
    backgroundColor: colors.indigo50,
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.indigo100,
  },
  allUsedEmoji: {
    fontSize: 32,
    marginBottom: 12,
  },
  allUsedText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.indigo700,
    textAlign: 'center',
    marginBottom: 8,
  },
  allUsedHint: {
    fontSize: 14,
    color: colors.slate600,
    textAlign: 'center',
  },
});
